/**
 * Keyboard layout definitions for TvGridKeyboardComponent
 * Each key can be a character or a special action key
 */

export interface KeyboardKey {
  /** Display label for the key */
  label: string;
  /** Value to type (for character keys) or action name (for special keys) */
  value: string;
  /** Whether this is a special action key vs. regular character */
  isAction?: boolean;
  /** Width multiplier (1 = normal, 2 = double width, etc.) */
  width?: number;
}

export type KeyboardRow = KeyboardKey[];
export type KeyboardLayout = KeyboardRow[];

/**
 * QWERTY layout with numbers, letters, and special characters
 * Similar to YouTube TV keyboard layout
 */
export const QWERTY_LAYOUT: KeyboardLayout = [
  // Row 1: Numbers and symbols
  [
    { label: '1', value: '1' },
    { label: '2', value: '2' },
    { label: '3', value: '3' },
    { label: '4', value: '4' },
    { label: '5', value: '5' },
    { label: '6', value: '6' },
    { label: '7', value: '7' },
    { label: '8', value: '8' },
    { label: '9', value: '9' },
    { label: '0', value: '0' }
  ],
  // Row 2: Top letter row + backspace
  [
    { label: 'Q', value: 'Q' },
    { label: 'W', value: 'W' },
    { label: 'E', value: 'E' },
    { label: 'R', value: 'R' },
    { label: 'T', value: 'T' },
    { label: 'Y', value: 'Y' },
    { label: 'U', value: 'U' },
    { label: 'I', value: 'I' },
    { label: 'O', value: 'O' },
    { label: 'P', value: 'P' },
    { label: '⌫', value: 'BACKSPACE', isAction: true }
  ],
  // Row 3: Middle letter row
  [
    { label: 'A', value: 'A' },
    { label: 'S', value: 'S' },
    { label: 'D', value: 'D' },
    { label: 'F', value: 'F' },
    { label: 'G', value: 'G' },
    { label: 'H', value: 'H' },
    { label: 'J', value: 'J' },
    { label: 'K', value: 'K' },
    { label: 'L', value: 'L' }
  ],
  // Row 4: Bottom letter row + clear
  [
    { label: 'Z', value: 'Z' },
    { label: 'X', value: 'X' },
    { label: 'C', value: 'C' },
    { label: 'V', value: 'V' },
    { label: 'B', value: 'B' },
    { label: 'N', value: 'N' },
    { label: 'M', value: 'M' },
    { label: 'Clear', value: 'CLEAR', isAction: true, width: 2 }
  ],
  // Row 5: Special characters and space
  [
    { label: '@', value: '@' },
    { label: '.', value: '.' },
    { label: '_', value: '_' },
    { label: '-', value: '-' },
    { label: 'Space', value: ' ', width: 4 },
    { label: 'Done', value: 'DONE', isAction: true, width: 2 }
  ]
];

/**
 * Numeric layout for phone numbers and codes
 * Simplified 3x4 grid like phone dialpad
 */
export const NUMERIC_LAYOUT: KeyboardLayout = [
  // Row 1
  [
    { label: '1', value: '1' },
    { label: '2', value: '2' },
    { label: '3', value: '3' }
  ],
  // Row 2
  [
    { label: '4', value: '4' },
    { label: '5', value: '5' },
    { label: '6', value: '6' }
  ],
  // Row 3
  [
    { label: '7', value: '7' },
    { label: '8', value: '8' },
    { label: '9', value: '9' }
  ],
  // Row 4: 0 and actions
  [
    { label: '⌫', value: 'BACKSPACE', isAction: true },
    { label: '0', value: '0' },
    { label: 'Done', value: 'DONE', isAction: true }
  ]
];

/**
 * Email layout with common email characters
 */
export const EMAIL_LAYOUT: KeyboardLayout = [
  // Row 1: Numbers
  [
    { label: '1', value: '1' },
    { label: '2', value: '2' },
    { label: '3', value: '3' },
    { label: '4', value: '4' },
    { label: '5', value: '5' },
    { label: '6', value: '6' },
    { label: '7', value: '7' },
    { label: '8', value: '8' },
    { label: '9', value: '9' },
    { label: '0', value: '0' }
  ],
  // Row 2: Top letter row
  [
    { label: 'Q', value: 'Q' },
    { label: 'W', value: 'W' },
    { label: 'E', value: 'E' },
    { label: 'R', value: 'R' },
    { label: 'T', value: 'T' },
    { label: 'Y', value: 'Y' },
    { label: 'U', value: 'U' },
    { label: 'I', value: 'I' },
    { label: 'O', value: 'O' },
    { label: 'P', value: 'P' },
    { label: '⌫', value: 'BACKSPACE', isAction: true }
  ],
  // Row 3: Middle letter row
  [
    { label: 'A', value: 'A' },
    { label: 'S', value: 'S' },
    { label: 'D', value: 'D' },
    { label: 'F', value: 'F' },
    { label: 'G', value: 'G' },
    { label: 'H', value: 'H' },
    { label: 'J', value: 'J' },
    { label: 'K', value: 'K' },
    { label: 'L', value: 'L' }
  ],
  // Row 4: Bottom letter row
  [
    { label: 'Z', value: 'Z' },
    { label: 'X', value: 'X' },
    { label: 'C', value: 'C' },
    { label: 'V', value: 'V' },
    { label: 'B', value: 'B' },
    { label: 'N', value: 'N' },
    { label: 'M', value: 'M' },
    { label: 'Clear', value: 'CLEAR', isAction: true, width: 2 }
  ],
  // Row 5: Email-specific characters
  [
    { label: '@', value: '@' },
    { label: '.', value: '.' },
    { label: '_', value: '_' },
    { label: '-', value: '-' },
    { label: 'Space', value: ' ', width: 3 },
    { label: 'Done', value: 'DONE', isAction: true, width: 2 }
  ]
];
